import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from './store';

/** Params slice — granular selectors avoid re-renders when unrelated fields change. */
const selectParamsState = (state: RootState) => state.params;

export const selectParams = selectParamsState;

export const selectParamsFilter = createSelector(selectParamsState, (params) => params.filter);

export const selectParamsSortBy = createSelector(selectParamsState, (params) => params.sortby);

export const selectParamsPageNumber = createSelector(selectParamsState, (params) => params.pageNumber);

/** @deprecated Use `selectParams` — kept for gradual migration */
export const ParamsInRedux = selectParams;

/** User slice */
const selectUserState = (state: RootState) => state.user.user;

export const selectUser = selectUserState;

export const selectUserId = createSelector(selectUserState, (user) => user.id);

export const selectUserName = createSelector(selectUserState, (user) => user.userName);

export const selectIsLoggedIn = (state: RootState) => state.user.isLoggedIn;

export const selectIsTokenExpired = (state: RootState) => state.user.isTokenExpired;

export const selectUserRole = createSelector(selectUserState, (user) => {
    const raw = (user?.userRole as string | undefined) ?? '';
    const normalized = raw.trim().toLowerCase();
    if (normalized === 'admin') return 'admin';
    if (normalized === 'user') return 'user';
    return 'user';
});

export const selectUserLoading = (state: RootState) => state.user.isLoading;

export const selectAccessToken = createSelector(selectUserState, (user) => user.accessToken);

/** Currency slice */
const selectCurrencyState = (state: RootState) => state.currency;

export const selectSelectedCurrency = createSelector(selectCurrencyState, (currency) => currency.selected);

export const selectCurrencyAvailable = createSelector(selectCurrencyState, (currency) => currency.available);

export const selectCurrencyStatus = createSelector(selectCurrencyState, (currency) => currency.status);

/** Product list slice */
const selectProductListState = (state: RootState) => state.productList;

export const selectProductListItems = createSelector(selectProductListState, (list) => list.items);

export const selectProductListStatus = createSelector(selectProductListState, (list) => list.status);

export const selectIsAddMoreLoading = createSelector(selectProductListState, (list) => list.isAddingMoreItems);

export const selectProductListHasMore = createSelector(selectProductListState, (list) => list.hasMore);

export const selectCurrentSearch = createSelector(selectProductListState, (list) => list.currentSearch);

export const selectCurrentCategory = createSelector(selectProductListState, (list) => list.currentCategory);

export const selectIsSearch = createSelector(selectProductListState, (list) => list.isSearch);

/** Inputs merged when Home builds a product-list API query. */
export const selectHomeFetchInputs = createSelector(
    [selectParamsState, selectUserId, selectSelectedCurrency],
    (params, userId, showCurrency) => ({
        params,
        userId,
        showCurrency: showCurrency || 'UZS'
    })
);
